<template>
  <v-app>
    <v-navigation-drawer
      v-model="drawer"
      color="secondary"
      permanent
      rail
      location="right"
      elevation="2"
      class="drawer-no-footer"
    >
      <v-list density="compact" nav>
        <v-list-item
          @click="
            drawer = false;
            historyDrawer = false;
          "
        >
          <template v-slot:prepend>
            <v-icon color="white" icon="mdi-close"></v-icon>
          </template>
        </v-list-item>
      </v-list>
      <v-divider></v-divider>
      <v-list density="compact" nav>
        <v-list-item @click="newChat()">
          <template v-slot:prepend>
            <v-avatar>
              <v-icon color="white" icon="mdi-chat-plus"></v-icon>
              <v-tooltip activator="parent" location="start">
                {{ $t("strings.new-chat") }}
              </v-tooltip>
            </v-avatar>
          </template>
        </v-list-item>
        <v-list-item @click="historyDrawer = true">
          <template v-slot:prepend>
            <v-avatar>
              <v-icon color="white" icon="mdi-history"></v-icon>
              <v-tooltip activator="parent" location="start">
                {{ $t("strings.view-history") }}
              </v-tooltip>
            </v-avatar>
          </template>
        </v-list-item>
        <v-list-item @click="historyDrawer = true" class="d-none">
          <template v-slot:prepend>
            <v-avatar>
              <v-icon color="white" icon="mdi-script-text-outline"></v-icon>
              <v-tooltip activator="parent" location="start">
                {{ $t("strings.view-system-logs") }}
              </v-tooltip>
            </v-avatar>
          </template>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
    <!-- History drawer (temporary overlay) -->
    <v-navigation-drawer
      v-model="historyDrawer"
      temporary
      rail-width="64"
      @click="rail = false"
      location="right"
      elevation="4"
      color="blue-grey-lighten-5"
      class="history-drawer border-0 drawer-no-footer ai-drawer"
      :width="400"
    >
      <template v-slot:prepend>
        <v-list density="compact" bg-color="primary" nav class="pa-0 mb-2">
          <v-list-item density="compact">
            <template v-slot:prepend>
              <v-avatar>
                <v-icon
                  icon="mdi-history"
                  bg-color="white"
                  color="white"
                  size="large"
                ></v-icon>
              </v-avatar>
            </template>
            <template v-slot:title>
              <div class="font-weight-bold text-h6 text--primary">
                {{ $t("labels.history") }}
              </div>
            </template>
            <template v-slot:append>
              <v-btn
                icon
                variant="text"
                color="white"
                @click="historyDrawer = false"
              >
                <v-icon icon="mdi-close"></v-icon>
              </v-btn>
            </template>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item>
            <template v-slot:append>
              <v-btn
                variant="outlined"
                color="white"
                size="x-small"
                @click="historyMessages = []"
                >{{ $t("labels.clear") }}</v-btn
              >
            </template>
          </v-list-item>
        </v-list>
      </template>
      <template v-slot:append>
        <p>&NonBreakingSpace;&nbsp;</p>
      </template>
      <template v-slot:default>
        <v-timeline
          density="compact"
          truncate-line="both"
          class="pa-4"
          align="start"
          side="end"
          size="default"
          direction="vertical"
          line-thickness="1"
        >
          <v-timeline-item
            v-for="(h, idx) in historyMessages"
            :key="idx"
            :dot-color="roleColor(h.role)"
            :icon="roleIcon(h.role)"
            :fill-dot="false"
            class="pb-1"
          >
            <v-card class="mb-1">
              <v-card-text class="bg-white text--primary">
                <p class="text-body-2 mt-1">{{ h.text }}</p>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </template>
    </v-navigation-drawer>
    <!-- Chat drawer -->
    <v-navigation-drawer
      v-model="drawer"
      :rail="rail"
      rail-width="64"
      permanent
      @click="rail = false"
      location="right"
      elevation="4"
      color="blue-grey-lighten-5"
      class="border-0 drawer-no-footer ai-drawer"
      :width="400"
    >
      <template v-slot:prepend>
        <v-list density="compact" bg-color="primary" nav class="pa-0 mb-2">
          <v-list-item density="compact">
            <template v-if="!rail" v-slot:prepend>
              <v-avatar>
                <v-icon
                  icon="mdi-creation"
                  bg-color="white"
                  color="white"
                  size="large"
                ></v-icon>
              </v-avatar>
            </template>
            <template v-else v-slot:prepend>
              <v-btn
                icon="mdi-chevron-left"
                variant="text"
                color="white"
                @click.stop="rail = !rail"
              ></v-btn>
            </template>
            <template v-if="!rail" v-slot:title>
              <div class="font-weight-bold text-h6 text--primary">
                {{ $t("strings.ai-assistant") }}
              </div>
            </template>
            <template v-slot:append>
              <v-btn
                icon="mdi-chevron-right"
                variant="text"
                color="white"
                @click.stop="rail = !rail"
              ></v-btn>
            </template>
          </v-list-item>
        </v-list>
      </template>
      <template v-if="!rail" v-slot:append>
        <v-textarea
          v-model="userRequest"
          :placeholder="t('strings.ask-assistant')"
          variant="outlined"
          bg-color="white"
          density="compact"
          class="mx-2 my-2 text-caption"
          rows="3"
          no-resize
          hide-details
          :disabled="!isConnecting || !socket?.connected"
          @keydown.enter.exact.prevent="sendMessage"
        >
          <template v-slot:append-inner>
            <v-btn
              v-if="userRequest.trim() && socket?.connected"
              color="primary"
              icon="mdi-send"
              variant="flat"
              class="mt-10"
              density="compact"
              :disabled="!userRequest.trim() || !isConnecting"
              @click="sendMessage"
            >
              <v-icon size="small"></v-icon>
            </v-btn>
            <v-progress-circular
              v-else-if="!isConnecting"
              indeterminate
              size="24"
              color="primary"
              class="mt-10"
            ></v-progress-circular>
          </template>
        </v-textarea>
      </template>

      <v-timeline
        id="chat-container"
        class="pa-4"
        align="start"
        side="end"
        density="compact"
        size="default"
        truncate-line="both"
        direction="vertical"
        line-thickness="1"
      >
        <v-timeline-item
          v-for="(msg, i) in messages"
          :key="i"
          :dot-color="roleColor(msg.role)"
          :icon="roleIcon(msg.role)"
          :fill-dot="false"
          :id="`msg-${i}`"
        >
          <template v-slot:opposite>
            <span>
              {{ msg.role === "user" ? $t("labels.you") : $t("labels.ai") }}
            </span>
          </template>
          <v-card class="mb-2">
            <v-card-text class="bg-white text--primary pa-2">
              <div class="text-body-2">
                {{ msg.text }}
              </div>
            </v-card-text>
          </v-card>
        </v-timeline-item>
      </v-timeline>
    </v-navigation-drawer>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { io } from "socket.io-client";

const { t } = useI18n();
const urlPath = window.location.pathname;

// Define props and emits for the component
const props = defineProps({
  apiChat: {
    type: String,
    required: true,
  },
  tenant: {
    type: String,
    required: true,
  },
  showDrawerAssistant: {
    type: Boolean,
    required: true,
  },
});
// Get configuration from app context
const CONFIG = {
  TENANT_ID: props.tenant,
  API_BASE_URL: props.apiChat,
  PROJECT_ID: urlPath.split("draw/").pop().split("/v")[0],
  VERSION_ID: urlPath.split("v/").pop().split("/edit")[0],
};

const emit = defineEmits(["toggleDrawerAssistant", "refreshMashup"]);

const drawer = computed({
  get: () => props.showDrawerAssistant,
  set: (val) => {
    emit("toggleDrawerAssistant", val);
  },
});

const rail = ref(false);
const userRequest = ref("");
const isConnecting = ref(false);
const historyDrawer = ref(false);
// Chat Ready message array
const readyMessage = ref([
  {
    role: "assistant",
    text: t("strings.welcome-message"),
  },
]);
// Chat messages array
const messages = ref([]);
// History messages (separate drawer)
const historyMessages = ref([]);

// When messages change, scroll the chat to the bottom
watch(
  messages,
  () => {
    nextTick(() => {
      // Find the scrollable drawer content
      const drawerContent =
        document.querySelector("#chat-container").parentElement;
      if (drawerContent) {
        setTimeout(() => {
          drawerContent.scrollTop = drawerContent.scrollHeight;
        }, 50);
      }
    });
  },
  { deep: true },
);

// Role -> color and icon map
const roleProps = {
  assistant: { color: "primary", icon: "mdi-robot-outline" },
  user: { color: "secondary", icon: "mdi-face-man-outline" },
  AIMessage: { color: "grey", icon: "mdi-robot-outline" },
  HumanMessage: { color: "grey", icon: "mdi-face-man-outline" },
  ToolMessage: { color: "grey", icon: "mdi-creation" },
};

// Helpers to safely read role properties with a fallback
function roleColor(role) {
  return (
    (role && roleProps[role] && roleProps[role].color) ||
    roleProps.assistant.color
  );
}

function roleIcon(role) {
  return (
    (role && roleProps[role] && roleProps[role].icon) ||
    roleProps.assistant.icon
  );
}

let socket = null;

onMounted(() => {
  console.log("SidebarAssistant mounted");
});

onUnmounted(() => {
  if (socket) {
    socket.disconnect();
    socket = null;
    console.log("SidebarAssistant unmounted");
  }
});

// Error toast
function showErrorToast(message) {
  messages.value.push({
    role: "assistant",
    text: `🚫 ${message}`,
  });
  console.error(message);
}

function newChat() {
  disconnect();
  messages.value = [];
  userRequest.value = "";
  connectSocket();
}

function disconnect() {
  if (socket) {
    socket.disconnect();
  }
  isConnecting.value = false;
}

// Connect to Socket.IO /chat namespace
function connectSocket() {
  if (typeof io !== "function") {
    throw new Error("socket.io client not available (import failed)");
  }

  const query = {
    tenantId: CONFIG.TENANT_ID,
    projectId: CONFIG.PROJECT_ID,
    versionId: CONFIG.VERSION_ID,
  };

  socket = io(`${CONFIG.API_BASE_URL}`, {
    transports: ["websocket"],
    withCredentials: true,
    query,
  });

  // Receive chat history only for HumanMessage and AIMessage
  socket.on("history", (messageHistory) => {
    console.log("Received chat history:", messageHistory);
    if (Array.isArray(messageHistory)) {
      messageHistory.forEach((msg) => {
        // Include only HumanMessage and AIMessage, exclude ToolMessage
        if (msg.type === "HumanMessage" || msg.type === "AIMessage") {
          const content =
            typeof msg?.content === "string"
              ? msg.content
              : JSON.stringify(msg);
          const entry = { role: msg.type, text: content };
          historyMessages.value.push(entry);
        }
      });
    }
  });

  // Connection established
  socket.on("connect", () => {
    console.log(`✓ Socket connected: ${socket.id}`);
    isConnecting.value = true;
    messages.value.push(...readyMessage.value);
  });

  // Disconnected
  socket.on("disconnect", (reason) => {
    console.log("Socket disconnected:", reason);
    messages.value.push({
      role: "assistant",
      text: `Disconnected: ${reason}`,
    });
  });

  // Connection error
  socket.on("connect_error", (err) => {
    showErrorToast(`Connection error: ${err?.message || err}`);
  });

  // Server error event
  socket.on("error", (err) => {
    showErrorToast(`Server error: ${err?.message ?? JSON.stringify(err)}`);
  });

  // Receive bot response
  socket.on("bot", (msg) => {
    const content =
      typeof msg?.content === "string" ? msg.content : JSON.stringify(msg);
    messages.value.push({
      role: "assistant",
      text: content,
    });
  });
  socket.on("signal", (sig) => {
    console.log("Signal: ", sig);

    messages.value.push({
      role: "assistant",
      text: t("strings.update-mashup"),
    });
    emit("refreshMashup");
  });
}

// Setup Socket.IO connection with OAuth2
async function setupSocketIO() {
  try {
    //Connect to Socket.IO (no token required)
    connectSocket();
  } catch (error) {
    isConnecting.value = false;
    showErrorToast(error?.message || "Failed to connect to assistant");
  }
}

watch(
  drawer,
  (isOpen) => {
    if (isOpen && (!socket || !socket.connected) && !isConnecting.value) {
      setupSocketIO();
    }
    if (!isOpen && socket && socket.connected) {
      disconnect();
    }
  },
  { immediate: false },
);

// Send message via Socket.IO
function sendMessage() {
  const text = userRequest.value.trim();
  if (!text || !socket || !socket.connected) {
    if (!socket?.connected) {
      showErrorToast("Socket not connected");
    }
    return;
  }
  // Add user message to chat
  messages.value.push({
    role: "user",
    text,
  });
  // Send to server
  socket.emit("message", {
    projectId: CONFIG.PROJECT_ID,
    versionId: CONFIG.VERSION_ID,
    message: text,
  });
  userRequest.value = "";
}
</script>

<style scoped>
:deep(.drawer-no-footer) {
  height: calc(100% - 200px) !important;
}
.ai-drawer::-webkit-scrollbar {
  width: 4px;
}
.ai-drawer::-webkit-scrollbar-thumb {
  background: #bdbdbd;
  border-radius: 4px;
}
.ai-drawer::-webkit-scrollbar-track {
  background: transparent;
}
.ai-drawer {
  scrollbar-width: thin;
  scrollbar-color: #bdbdbd transparent;
}
</style>
